import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { useVessel } from '../../../../contexts/VesselContext';
import { useCrew } from '../../../../hooks/useCrew';
import { displayName } from '../crew/viewHelpers';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';
// FT-357: shell-level back handler — bottom bar owns Back. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

const LogRepairView = ({ onBack, onSave, initialData = null }) => {
  // FT-357: register onBack with the shell-level MobileBottomBar.
  useSetBackHandler(onBack);

  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { crew } = useCrew();
  const { containerRef, swipeProgress } = useSwipeBack(onBack);
  
  const [formData, setFormData] = useState({
    date_reported: new Date().toISOString().split('T')[0],
    reported_by: '',
    description: '',
    location: '',
    risk_level: 'Service', // Work type for repairs
    equipment_linked: '',
    parts_used: '',
    contractor_details: '',
    repair_cost: '',
    repair_details: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  // Populate form when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        date_reported: initialData.date_reported || new Date().toISOString().split('T')[0],
        reported_by: initialData.reported_by || '',
        description: initialData.description || '',
        location: initialData.location || '',
        risk_level: initialData.risk_level || 'Service',
        equipment_linked: initialData.equipment_linked || '',
        parts_used: initialData.parts_used || '',
        contractor_details: initialData.contractor_details || '',
        repair_cost: initialData.repair_cost || '',
        repair_details: initialData.repair_details || '',
        notes: initialData.notes || ''
      });
    }
  }, [initialData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.reported_by) {
      newErrors.reported_by = 'Completed by is required';
    }
    if (!formData.date_reported) {
      newErrors.date_reported = 'Date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }
    
    // Create repair record
    const repairRecord = {
      ...formData,
      id: initialData?.id || `REP-${Date.now()}`,
      record_type: 'repair',
      status: 'Closed', // Repairs are always closed
      fixed_immediately: true, // Repairs are completed work
      created_at: initialData?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      history: initialData?.history || [{
        date: new Date().toISOString(),
        action: 'Repair Logged',
        by: formData.reported_by,
        notes: 'Repair/service completed and logged'
      }]
    };
    
    onSave(repairRecord);
  };

  return (
    <div className="min-h-screen bg-gray-50" ref={containerRef}>
      <SwipeIndicator progress={swipeProgress} />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* FT-357: header BackButton removed — bottom bar owns Back. */}
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {initialData ? 'Edit Repair' : 'Log Repair/Service'}
                </h1>
                <p className="text-sm text-gray-500">
                  {currentVessel?.name || 'Maintenance Log'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      {/* FT-83: pb-24 md:pb-0 prevents Save/Cancel buttons from being hidden behind mobile bottom nav */}
      <form onSubmit={handleSubmit}>
        <div className="max-w-3xl mx-auto p-6 pb-24 md:pb-6 space-y-6">
          
          {/* Basic Information Section */}
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-950/5 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Basic Information</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Date Completed */}
              <div>
                <label htmlFor="date_reported" className="block text-sm font-medium text-gray-700 mb-1">
                  Date Completed <span className="text-red-500">*</span>
                </label>
                <input
                  id="date_reported"
                  type="date"
                  value={formData.date_reported}
                  onChange={(e) => handleInputChange('date_reported', e.target.value)}
                  className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                    errors.date_reported ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                />
                {errors.date_reported && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.date_reported}
                  </p>
                )}
              </div>

              {/* Completed By */}
              <div>
                <label htmlFor="reported_by" className="block text-sm font-medium text-gray-700 mb-1">
                  Completed By <span className="text-red-500">*</span>
                </label>
                <select
                  id="reported_by"
                  value={formData.reported_by}
                  onChange={(e) => handleInputChange('reported_by', e.target.value)}
                  className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 bg-white ring-1 ring-inset ${
                    errors.reported_by ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                >
                  <option value="">Select crew member</option>
                  {crew && crew.map(member => (
                    <option key={member.id} value={displayName(member)}>
                      {displayName(member)}
                    </option>
                  ))}
                </select>
                {errors.reported_by && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.reported_by}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Repair Details Section */}
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-950/5 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Repair Details</h2>
            
            <div className="space-y-4">
              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  placeholder="Describe what was repaired or serviced..."
                  className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                    errors.description ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.description}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Include what work was done and why
                </p>
              </div>

              {/* Location and Work Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Location */}
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    id="location"
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="e.g., Engine room, Bridge, Deck"
                    className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                  />
                </div>

                {/* Work Type */}
                <div>
                  <label htmlFor="risk_level" className="block text-sm font-medium text-gray-700 mb-1">
                    Work Type
                  </label>
                  <select
                    id="risk_level"
                    value={formData.risk_level}
                    onChange={(e) => handleInputChange('risk_level', e.target.value)}
                    className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 bg-white ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                  >
                    <option value="Preventive">Preventive Maintenance</option>
                    <option value="Corrective">Corrective Repair</option>
                    <option value="Emergency">Emergency Repair</option>
                    <option value="Service">Routine Service</option>
                  </select>
                </div>
              </div>

              {/* Equipment Linked */}
              <div>
                <label htmlFor="equipment_linked" className="block text-sm font-medium text-gray-700 mb-1">
                  Equipment Linked
                </label>
                <input
                  id="equipment_linked"
                  type="text"
                  value={formData.equipment_linked}
                  onChange={(e) => handleInputChange('equipment_linked', e.target.value)}
                  placeholder="e.g., Main engine, Navigation lights, Fire pump"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Link to specific equipment if applicable
                </p>
              </div>
            </div>
          </div>

          {/* Repair Information Section */}
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-950/5 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Repair Information</h2>
            
            <div className="space-y-4">
              {/* Parts Used */}
              <div>
                <label htmlFor="parts_used" className="block text-sm font-medium text-gray-700 mb-1">
                  Parts Used
                </label>
                <textarea
                  id="parts_used"
                  value={formData.parts_used}
                  onChange={(e) => handleInputChange('parts_used', e.target.value)}
                  rows={2}
                  placeholder="List parts with part numbers, quantities, suppliers..."
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Include part numbers for future reference
                </p>
              </div>

              {/* Contractor and Cost */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Contractor/Business */}
                <div>
                  <label htmlFor="contractor_details" className="block text-sm font-medium text-gray-700 mb-1">
                    Contractor/Business
                  </label>
                  <input
                    id="contractor_details"
                    type="text"
                    value={formData.contractor_details}
                    onChange={(e) => handleInputChange('contractor_details', e.target.value)}
                    placeholder="Name and contact details"
                    className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                  />
                </div>

                {/* Repair Cost */}
                <div>
                  <label htmlFor="repair_cost" className="block text-sm font-medium text-gray-700 mb-1">
                    Repair Cost
                  </label>
                  <input
                    id="repair_cost"
                    type="text"
                    value={formData.repair_cost}
                    onChange={(e) => handleInputChange('repair_cost', e.target.value)}
                    placeholder="Total cost including parts and labor"
                    className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                  />
                </div>
              </div>

              {/* Repair Method */}
              <div>
                <label htmlFor="repair_details" className="block text-sm font-medium text-gray-700 mb-1">
                  Repair Method
                </label>
                <textarea
                  id="repair_details"
                  value={formData.repair_details}
                  onChange={(e) => handleInputChange('repair_details', e.target.value)}
                  rows={2}
                  placeholder="Describe how the repair was carried out..."
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Additional Notes Section */}
          <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-950/5 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Additional Notes</h2>
            
            <div>
              {/* Notes */}
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={2}
                placeholder="Any other relevant information..."
                className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
              />
            </div>
          </div>

          {/* Compliance Note Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This repair log helps track maintenance history and costs. 
              Small operators can use this for simple record-keeping without the full defect workflow.
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 sticky bottom-0 bg-gray-50 py-4">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-transparent rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              {initialData ? 'Update Repair' : 'Log Repair'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LogRepairView;